import { diffLines, diffWordsWithSpace, type Change } from 'diff';

/** Lines of unchanged context kept around each edit, matching GitHub's default. */
const CONTEXT_LINES = 3;

export interface DiffSpan {
  text: string;
  changed: boolean;
}

export interface GithubDiffLine {
  kind: 'context' | 'add' | 'del';
  text: string;
  oldNumber: number | null;
  newNumber: number | null;
  spans: DiffSpan[];
}

export interface GithubDiffHunk {
  header: string;
  lines: GithubDiffLine[];
}

/** One row of the side-by-side view. A null side is the gap opposite an insert or delete. */
export interface SplitDiffRow {
  left: GithubDiffLine | null;
  right: GithubDiffLine | null;
}

export interface GithubFileDiff {
  path: string;
  additions: number;
  deletions: number;
  hunks: GithubDiffHunk[];
}

export interface DiffFileInput {
  path: string;
  before: string;
  after: string;
}

interface NumberedLine {
  kind: GithubDiffLine['kind'];
  text: string;
  oldNumber: number | null;
  newNumber: number | null;
}

function changeKind(change: Change): GithubDiffLine['kind'] {
  if (change.added) return 'add';
  if (change.removed) return 'del';
  return 'context';
}

/** `diff` keeps the trailing newline inside `value`; the displayed line does not. */
function linesOf(value: string): string[] {
  if (value.length === 0) return [];
  const parts = value.split('\n');
  if (value.endsWith('\n')) parts.pop();
  return parts;
}

function numberLines(before: string, after: string): NumberedLine[] {
  let oldNumber = 1;
  let newNumber = 1;
  const lines: NumberedLine[] = [];
  for (const change of diffLines(before, after)) {
    const kind = changeKind(change);
    for (const text of linesOf(change.value)) {
      lines.push({
        kind,
        text,
        oldNumber: kind === 'add' ? null : oldNumber,
        newNumber: kind === 'del' ? null : newNumber,
      });
      if (kind !== 'add') oldNumber += 1;
      if (kind !== 'del') newNumber += 1;
    }
  }
  return lines;
}

function plainLine(line: NumberedLine): GithubDiffLine {
  return {
    ...line,
    spans: [{ text: line.text, changed: line.kind !== 'context' }],
  };
}

function wordSpans(
  before: string,
  after: string,
  side: 'del' | 'add',
): DiffSpan[] {
  return diffWordsWithSpace(before, after).flatMap((part) => {
    if (side === 'del' && part.added) return [];
    if (side === 'add' && part.removed) return [];
    if (part.value.length === 0) return [];
    const changed =
      side === 'del' ? part.removed === true : part.added === true;
    return [{ text: part.value, changed }];
  });
}

interface ReplacementRun {
  delStart: number;
  addStart: number;
  end: number;
}

function runEnd(
  lines: NumberedLine[],
  start: number,
  kind: 'del' | 'add',
): number {
  let index = start;
  while (lines[index]?.kind === kind) index += 1;
  return index;
}

/** A deletion run followed by an equal-length addition run, or null. */
function replacementRun(
  lines: NumberedLine[],
  start: number,
): ReplacementRun | null {
  if (lines[start]?.kind !== 'del') return null;
  const addStart = runEnd(lines, start, 'del');
  const end = runEnd(lines, addStart, 'add');
  if (addStart - start !== end - addStart) return null;
  return { delStart: start, addStart, end };
}

function lineAt(
  lines: NumberedLine[],
  result: GithubDiffLine[],
  index: number,
): { line: NumberedLine; painted: GithubDiffLine } | null {
  const line = lines[index];
  const painted = result[index];
  if (!line || !painted) return null;
  return { line, painted };
}

function paintWordDiff(
  result: GithubDiffLine[],
  lines: NumberedLine[],
  delIndex: number,
  addIndex: number,
): void {
  const left = lineAt(lines, result, delIndex);
  const right = lineAt(lines, result, addIndex);
  if (!left || !right) return;
  result[delIndex] = {
    ...left.painted,
    spans: wordSpans(left.line.text, right.line.text, 'del'),
  };
  result[addIndex] = {
    ...right.painted,
    spans: wordSpans(left.line.text, right.line.text, 'add'),
  };
}

/**
 * Mark the words that differ when a removed line is replaced by one added line.
 * Unequal runs stay whole-line highlights: pairing them by index would mark the
 * wrong words.
 */
function applyWordDiffs(lines: NumberedLine[]): GithubDiffLine[] {
  const result = lines.map(plainLine);
  let index = 0;
  while (index < lines.length) {
    const run = replacementRun(lines, index);
    if (!run) {
      index += 1;
      continue;
    }
    const count = run.addStart - run.delStart;
    for (let offset = 0; offset < count; offset += 1) {
      paintWordDiff(
        result,
        lines,
        run.delStart + offset,
        run.addStart + offset,
      );
    }
    index = run.end;
  }
  return result;
}

function hunkRanges(lines: GithubDiffLine[]): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  lines.forEach((line, index) => {
    if (line.kind === 'context') return;
    const start = Math.max(0, index - CONTEXT_LINES);
    const end = Math.min(lines.length - 1, index + CONTEXT_LINES);
    const last = ranges.at(-1);
    if (last && start <= last[1] + 1) {
      last[1] = Math.max(last[1], end);
      return;
    }
    ranges.push([start, end]);
  });
  return ranges;
}

function hunkHeader(lines: GithubDiffLine[]): string {
  const oldNumbers = lines.flatMap((line) =>
    line.oldNumber === null ? [] : [line.oldNumber],
  );
  const newNumbers = lines.flatMap((line) =>
    line.newNumber === null ? [] : [line.newNumber],
  );
  const oldStart = oldNumbers[0] ?? 0;
  const newStart = newNumbers[0] ?? 0;
  return `@@ -${String(oldStart)},${String(oldNumbers.length)} +${String(newStart)},${String(newNumbers.length)} @@`;
}

export function buildGithubFileDiff(file: DiffFileInput): GithubFileDiff {
  const lines = applyWordDiffs(numberLines(file.before, file.after));
  const hunks = hunkRanges(lines).map(([start, end]) => {
    const hunkLines = lines.slice(start, end + 1);
    return { header: hunkHeader(hunkLines), lines: hunkLines };
  });
  return {
    path: file.path,
    additions: lines.filter((line) => line.kind === 'add').length,
    deletions: lines.filter((line) => line.kind === 'del').length,
    hunks,
  };
}

/** Zip a deletion run with the addition run that replaces it, so the two columns line up. */
export function splitHunkRows(lines: GithubDiffLine[]): SplitDiffRow[] {
  const rows: SplitDiffRow[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line) break;
    if (line.kind === 'context') {
      rows.push({ left: line, right: line });
      index += 1;
      continue;
    }
    if (line.kind === 'add') {
      rows.push({ left: null, right: line });
      index += 1;
      continue;
    }
    const delStart = index;
    while (lines[index]?.kind === 'del') index += 1;
    const addStart = index;
    while (lines[index]?.kind === 'add') index += 1;
    const removed = lines.slice(delStart, addStart);
    const added = lines.slice(addStart, index);
    const count = Math.max(removed.length, added.length);
    for (let offset = 0; offset < count; offset += 1) {
      rows.push({
        left: removed[offset] ?? null,
        right: added[offset] ?? null,
      });
    }
  }
  return rows;
}

export function githubDiffTotals(files: GithubFileDiff[]): {
  files: number;
  additions: number;
  deletions: number;
} {
  return files.reduce(
    (totals, file) => ({
      files: totals.files + (file.additions + file.deletions > 0 ? 1 : 0),
      additions: totals.additions + file.additions,
      deletions: totals.deletions + file.deletions,
    }),
    { files: 0, additions: 0, deletions: 0 },
  );
}
